import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

// Searchable multi-select for events
function EventPicker({ events, selectedIds, onChange }) {
    const [search, setSearch] = useState('');

    const q = search.toLowerCase();
    const filtered = search
        ? events.filter(e => {
            const dateStr = e.event_date
                ? new Date(e.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                : '';
            return [dateStr, e.venue_name, e.city, e.headliner_name, e.lineup].some(v => v && v.toLowerCase().includes(q));
          })
        : events;

    const toggle = (id) => {
        if (selectedIds.includes(id)) {
            onChange(selectedIds.filter(i => i !== id));
        } else {
            onChange([...selectedIds, id]);
        }
    };

    return (
        <div>
            <input
                type="text"
                placeholder="Search events by date, venue, city, artist..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={styles.input}
            />
            <div style={styles.eventPickerList}>
                {filtered.length === 0 ? (
                    <div style={styles.pickerEmpty}>No events match your search.</div>
                ) : filtered.map(e => {
                    const checked = selectedIds.includes(e.id);
                    const dateStr = e.event_date
                        ? new Date(e.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                        : '—';
                    const label = [dateStr, e.venue_name, e.venue_city].filter(Boolean).join(' · ')
                        + (e.lineup ? ` — ${e.lineup}` : '');
                    return (
                        <div
                            key={e.id}
                            onClick={() => toggle(e.id)}
                            style={{ ...styles.eventPickerRow, ...(checked ? styles.eventPickerRowChecked : {}) }}
                        >
                            <span style={styles.eventPickerCheck}>{checked ? '✓' : ''}</span>
                            <span style={styles.eventPickerLabel}>{label}</span>
                        </div>
                    );
                })}
            </div>
            {selectedIds.length > 0 && (
                <div style={styles.selectedCount}>{selectedIds.length} event{selectedIds.length !== 1 ? 's' : ''} selected</div>
            )}
        </div>
    );
}

// Multi-select searchable picker for promoters
function PromoterPicker({ promoters, selectedIds, onChange }) {
    const [search, setSearch] = useState('');
    const filtered = search
        ? promoters.filter(p => [p.name, p.city].some(v => v && v.toLowerCase().includes(search.toLowerCase())))
        : promoters;

    const toggle = (id) => {
        if (selectedIds.includes(id)) onChange(selectedIds.filter(i => i !== id));
        else onChange([...selectedIds, id]);
    };

    const selectedPromoters = promoters.filter(p => selectedIds.includes(p.id));

    return (
        <div>
            {selectedPromoters.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                    {selectedPromoters.map(p => (
                        <div key={p.id} style={{ backgroundColor: '#2c2c2c', border: '1px solid #bb86fc', borderRadius: '4px', padding: '4px 10px', fontSize: '13px', color: '#bb86fc', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {p.name}
                            <span onClick={() => toggle(p.id)} style={{ cursor: 'pointer', color: '#888' }}>✕</span>
                        </div>
                    ))}
                </div>
            )}
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    placeholder="Search promoters..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: search && filtered.length > 0 ? '4px 4px 0 0' : '4px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' }}
                    autoComplete="off"
                />
                {search && filtered.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#2c2c2c', border: '1px solid #444', borderTop: 'none', borderRadius: '0 0 4px 4px', maxHeight: '200px', overflowY: 'auto', zIndex: 100 }}>
                        {filtered.map(p => (
                            <div
                                key={p.id}
                                onMouseDown={() => { toggle(p.id); setSearch(''); }}
                                style={{ padding: '9px 14px', fontSize: '14px', color: selectedIds.includes(p.id) ? '#bb86fc' : '#e0e0e0', cursor: 'pointer', borderBottom: '1px solid #333', backgroundColor: selectedIds.includes(p.id) ? '#1e1e1e' : 'transparent' }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#383838'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = selectedIds.includes(p.id) ? '#1e1e1e' : 'transparent'}
                            >
                                {p.name}{p.city ? ` — ${p.city}` : ''}
                                {selectedIds.includes(p.id) && <span style={{ marginLeft: '8px' }}>✓</span>}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function AddFestival() {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [promoters, setPromoters] = useState([]);
    const [selectedPromoterIds, setSelectedPromoterIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [form, setForm] = useState({
        name: '',
        description: '',
        image_url: '',
        start_date: '',
        end_date: '',
    });
    const [selectedEventIds, setSelectedEventIds] = useState([]);

    useEffect(() => {
        document.title = 'SHARC - Add Festival';
        Promise.all([
            axios.get(`${config.API_URL}/events`),
            axios.get(`${config.API_URL}/promoters`),
        ]).then(([eventsRes, promotersRes]) => {
                setEvents(eventsRes.data || []);
                setPromoters(promotersRes.data.promoters || []);
                setLoading(false);
            })
            .catch(() => {
                setError('Failed to load events');
                setLoading(false);
            });
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, []);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) { setError('Festival name is required'); return; }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim() || null,
                image_url: form.image_url.trim() || null,
                start_date: form.start_date || null,
                end_date: form.end_date || null,
                event_ids: selectedEventIds,
                promoter_ids: selectedPromoterIds,
            };
            const res = await axios.post(`${config.API_URL}/festivals`, payload);
            const newSlug = res.data.festival ? res.data.festival.slug : res.data.id;
            navigate(`/festivals/${newSlug}`);
        } catch (err) {
            setError('Failed to save festival');
            setSaving(false);
        }
    };

    if (loading) return <div style={styles.message}>Loading...</div>;

    return (
        <div style={styles.container}>
            <Link to="/festivals" style={styles.backLink}>← Back to Festivals</Link>
            <h1 style={styles.pageTitle}>Add Festival</h1>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.form}>
                <div style={styles.field}>
                    <label style={styles.label}>Festival Name *</label>
                    <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleFormChange}
                        placeholder="e.g. Coachella, Bonnaroo..."
                        style={styles.input}
                    />
                </div>

                <div style={styles.twoCol}>
                    <div style={styles.field}>
                        <label style={styles.label}>Start Date</label>
                        <input type="date" name="start_date" value={form.start_date} onChange={handleFormChange} style={styles.input} />
                    </div>
                    <div style={styles.field}>
                        <label style={styles.label}>End Date</label>
                        <input type="date" name="end_date" value={form.end_date} onChange={handleFormChange} style={styles.input} />
                    </div>
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Promoter(s)</label>
                    <PromoterPicker promoters={promoters} selectedIds={selectedPromoterIds} onChange={setSelectedPromoterIds} />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Description</label>
                    <textarea
                        name="description"
                        value={form.description}
                        onChange={handleFormChange}
                        placeholder="Brief description of this festival..."
                        style={styles.textarea}
                        rows={4}
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Image URL</label>
                    <input
                        type="text"
                        name="image_url"
                        value={form.image_url}
                        onChange={handleFormChange}
                        placeholder="https://..."
                        style={styles.input}
                    />
                    {form.image_url && (
                        <img
                            src={form.image_url}
                            alt="Festival preview"
                            style={styles.imagePreview}
                            onError={e => { e.target.style.display = 'none'; }}
                        />
                    )}
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Link Events to This Festival</label>
                    <EventPicker events={events} selectedIds={selectedEventIds} onChange={setSelectedEventIds} />
                </div>

                <button onClick={handleSubmit} disabled={saving} style={styles.submitButton}>
                    {saving ? 'Saving...' : 'Save Festival'}
                </button>
            </div>
        </div>
    );
}

const styles = {
    container: { maxWidth: '700px', margin: '0 auto' },
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px', display: 'inline-block', marginBottom: '24px' },
    pageTitle: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '32px' },
    form: { display: 'flex', flexDirection: 'column', gap: '24px' },
    field: { display: 'flex', flexDirection: 'column', gap: '8px' },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    label: { fontSize: '14px', fontWeight: '600', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    textarea: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' },
    imagePreview: { width: '120px', height: '120px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #333', marginTop: '8px' },
    eventPickerList: { maxHeight: '280px', overflowY: 'auto', border: '1px solid #444', borderRadius: '6px', backgroundColor: '#1a1a1a', marginTop: '8px' },
    eventPickerRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #222', transition: 'background 0.1s' },
    eventPickerRowChecked: { backgroundColor: '#2c2c2c' },
    eventPickerCheck: { width: '16px', color: '#bb86fc', fontWeight: 'bold', flexShrink: 0 },
    eventPickerLabel: { fontSize: '13px', color: '#e0e0e0' },
    pickerEmpty: { padding: '20px', textAlign: 'center', color: '#666', fontSize: '13px' },
    selectedCount: { marginTop: '8px', fontSize: '13px', color: '#bb86fc', fontWeight: 'bold' },
    submitButton: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '6px', padding: '14px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', marginTop: '8px' },
    error: { backgroundColor: '#2a1a1a', border: '1px solid #cf6679', borderRadius: '6px', padding: '12px 16px', color: '#cf6679', fontSize: '14px', marginBottom: '16px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
};

export default AddFestival;
